/**
 * Formulário de um orçamento, com os itens dentro.
 *
 * Papel: preenche, valida e grava o cabeçalho da proposta — casa, categoria,
 * número, data, status, responsável, fornecedor, solicitante, modo de
 * pagamento e desconto — e a lista de itens, cada um com foto, descrição,
 * referência e valor.
 *
 * Como usar: quem monta este componente passa `orcamento` para editar ou `null`
 * para criar, e deverá dar a ele uma `key` diferente por registro. Sem a `key`,
 * o React reaproveita a mesma instância ao trocar de orçamento e os campos
 * continuam com o que a pessoa via antes. As casas do `<select>` chegam prontas
 * em `casas`, e a **casa pré-selecionada** e o **número sugerido** de um
 * registro novo em `casaSugerida` e `numeroSugerido`: os dois entram como
 * `defaultValues` do `useForm`, lidos **uma única vez, no primeiro render**, e
 * o react-hook-form não reage a uma prop que muda depois de o formulário
 * montar. Quem monta este componente para criar um orçamento precisa calcular
 * os três depois que `useResidencias` e a listagem de orçamentos responderem —
 * é o que `PaginaFormularioOrcamento` faz; montá-lo cedo demais trava a casa
 * em branco e o número em `001/AAAA`, e mais de um orçamento nasce com o mesmo
 * número.
 *
 * A `ref`, quando alguém a passa, recebe um `AlcaDoFormularioOrcamento` com
 * `obterValores`: quem chama pode ler os valores do formulário **sem esperar
 * o envio** — é o que `PaginaFormularioOrcamento` usa para duplicar o que
 * está na tela, edições não salvas inclusive, em vez de duplicar o último
 * registro gravado.
 *
 * **A imagem de um item já gravado não é recarregada ao abrir o formulário.**
 * O botão "Ver imagem" busca essa foto sob demanda, pela mesma rota que a
 * caixa da esquerda usaria para mostrá-la de imediato — o `id` da linha é o
 * que diz de qual item ela é. Escolher um arquivo novo substitui a foto
 * gravada ao salvar; não escolher nada preserva a que já existe, porque o
 * campo `foto` só entra no envio quando alguém troca a imagem. Um arquivo
 * maior do que `fotoDeEntradaSchema` aceita é recusado assim que escolhido,
 * com a mensagem do próprio schema; deixado para o `handleSubmit`, o envio
 * falharia calado, porque nada desenha o erro daquele campo.
 *
 * O total é recalculado a cada digitação por `totalDoOrcamento`, a mesma função
 * que a tabela usa. Ele não é campo e não é enviado. Como o item **não tem
 * quantidade**, o valor digitado em cada linha é o valor cheio dela.
 */
import {
  type ChangeEvent,
  type ReactNode,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import {
  type Orcamento,
  CATEGORIA_ORCAMENTO,
  MODO_PAGAMENTO_ORCAMENTO,
  STATUS_ORCAMENTO,
  dinheiroDoCampo,
  fotoDeEntradaSchema,
  novoOrcamentoSchema,
  totalDoOrcamento,
} from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, type FieldPath, useFieldArray, useForm, useWatch } from 'react-hook-form';

import { aplicarErroDaApi, tradutorPorMapa } from '@/shared/lib/erro-de-formulario';
import { formatarDinheiro } from '@/shared/lib/formato';
import { Button } from '@/shared/ui/button';
import { CabecalhoDeSecao } from '@/shared/ui/cabecalho-de-secao';
import { CampoMascarado } from '@/shared/ui/campo-mascarado';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import { Vazio } from '@/shared/ui/vazio';
import { useFotoDoItem, useOrcamentos, useSalvarOrcamento } from '../hooks/usar-orcamentos';
import { ROTULOS_CATEGORIA, ROTULOS_PAGAMENTO, ROTULOS_STATUS } from '../rotulos';
import {
  ITEM_EM_BRANCO,
  type ItemNoFormulario,
  ORCAMENTO_EM_BRANCO,
  type OrcamentoNoFormulario,
} from '../types';

/** Uma casa oferecida no `<select>`: a residência cadastrada ou a que um orçamento traz. */
export interface CasaDoOrcamento {
  id: string;
  apelido: string;
}

interface FormularioOrcamentoProps {
  /** O orçamento a editar, ou `null` para criar um novo. */
  orcamento: Orcamento | null;
  /** As casas que o orçamento pode receber, na ordem do `<select>`. */
  casas: readonly CasaDoOrcamento[];
  /** A casa já escolhida num registro novo; ignorada ao editar. */
  casaSugerida: string;
  /**
   * O número já calculado para um registro novo, contado pelo ano corrente.
   * Ignorado ao editar — o número mostrado é o do próprio `orcamento`.
   */
  numeroSugerido: string;
  onSalvo: () => void;
  onCancelar: () => void;
}

/**
 * O que `FormularioOrcamento` expõe a quem monta, pela `ref`.
 *
 * Existe porque "Duplicar", em `PaginaFormularioOrcamento`, precisa copiar o
 * que está na tela — inclusive edição que ninguém salvou ainda —, e o estado do
 * `useForm` mora dentro deste componente, fora do alcance da página.
 */
export interface AlcaDoFormularioOrcamento {
  /** Os valores do formulário no instante da chamada, antes do schema normalizar. */
  obterValores: () => OrcamentoNoFormulario;
}

/** Os campos que a API pode apontar, cada um no caminho do formulário. */
const CAMPOS: Record<string, FieldPath<OrcamentoNoFormulario> | undefined> = {
  residenciaId: 'residenciaId',
  numero: 'numero',
  data: 'data',
  status: 'status',
  categoria: 'categoria',
  fornecedor: 'fornecedor',
  responsavel: 'responsavel',
  solicitante: 'solicitante',
  pagamento: 'pagamento',
  desconto: 'desconto',
  observacao: 'observacao',
  itens: 'itens',
};

/**
 * O caminho de um campo de item dentro do formulário.
 *
 * Existe para o tipo do caminho sobreviver: interpolar o índice já convertido em
 * texto produziria `itens.${string}.descricao`, que o react-hook-form não
 * reconhece, e obrigaria cada campo a uma asserção.
 *
 * @param indice - A posição da linha na lista de itens.
 * @param campo - O campo daquele item.
 * @returns O caminho que `Controller` aceita em `name`.
 */
function caminhoDoItem<C extends keyof ItemNoFormulario>(indice: number, campo: C) {
  return `itens.${indice}.${campo}` as const;
}

/**
 * Lê o arquivo escolhido como uma imagem em base64, valida contra
 * `fotoDeEntradaSchema` e entrega o resultado ao formulário.
 *
 * Quem chama é a caixa de foto de cada item, ao trocar o arquivo do `<input
 * type="file">` escondido dentro dela. A validação acontece aqui, e não só no
 * `handleSubmit`, porque um arquivo grande demais recusado só ali faria o envio
 * falhar sem nenhum aviso na tela — nada desenha o erro daquele campo, e a
 * única pista seria o formulário não sair do lugar. Um arquivo que o
 * navegador não consegue ler — falha de `FileReader`, não de tamanho — cai no
 * mesmo `onInvalido`, com uma mensagem própria.
 *
 * @param evento - O evento de troca do campo de arquivo.
 * @param onLido - Chamado com a imagem em base64, já validada, pronta para o
 *   campo `foto`.
 * @param onInvalido - Chamado com a mensagem a mostrar quando o arquivo não
 *   passa no schema ou não pôde ser lido.
 */
function handleArquivoDoItem(
  evento: ChangeEvent<HTMLInputElement>,
  onLido: (imagem: string) => void,
  onInvalido: (mensagem: string) => void,
) {
  const arquivo = evento.target.files?.[0];
  if (arquivo === undefined) return;

  const leitor = new FileReader();
  leitor.onload = () => {
    if (typeof leitor.result !== 'string') return;
    const validado = fotoDeEntradaSchema.safeParse(leitor.result);
    if (!validado.success) {
      onInvalido(validado.error.issues[0]?.message ?? 'Não foi possível usar esta imagem.');
      return;
    }
    onLido(leitor.result);
  };
  leitor.onerror = () => {
    onInvalido('Não foi possível ler o arquivo.');
  };
  leitor.readAsDataURL(arquivo);
}

/**
 * O botão que busca e mostra a imagem já gravada de um item.
 *
 * A imagem não vem com a listagem — são megabytes somados —, e por isso a
 * requisição só sai quando alguém clica. Depois de carregada ela fica no cache
 * pelo tempo que `useFotoDoItem` define, e reabrir não custa nova requisição.
 *
 * @param itemId - O item cuja imagem se quer.
 * @returns O botão, e a imagem abaixo dele depois de carregada.
 */
function ImagemDoItem({ itemId }: { itemId: string }) {
  const [pedida, setPedida] = useState(false);
  const consulta = useFotoDoItem(pedida ? itemId : null);
  const foto = consulta.data?.foto ?? null;

  return (
    <div>
      {!pedida && (
        <Button type="button" size="xs" variant="ghost" onClick={() => setPedida(true)}>
          Ver imagem
        </Button>
      )}
      {pedida && consulta.isPending && <p className="text-xs text-texto-fraco">Carregando…</p>}
      {pedida && consulta.error !== null && (
        <p className="text-xs text-perigo">Não foi possível carregar a imagem.</p>
      )}
      {foto !== null && (
        <img
          src={foto}
          alt="Imagem do item"
          className="mt-1 max-h-48 rounded-sm border border-border"
        />
      )}
    </div>
  );
}

/** Um campo do cabeçalho, com o rótulo mono acima e o erro do schema abaixo. */
function CampoDoCabecalho({
  rotulo,
  htmlFor,
  erro,
  className,
  children,
}: {
  rotulo: string;
  htmlFor: string;
  erro?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className}>
      <Label htmlFor={htmlFor}>{rotulo}</Label>
      <div className="mt-1.5">{children}</div>
      {erro !== undefined && <p className="mt-1 text-xs text-perigo">{erro}</p>}
    </div>
  );
}

/**
 * O formulário de um orçamento.
 *
 * @param orcamento - O registro a editar, ou `null`.
 * @param casas - As casas oferecidas no `<select>`; quem monta já tirou delas o
 *   lugar que contém outra casa, e deixou a do registro em edição.
 * @param casaSugerida - A casa escolhida de saída num registro novo, a do
 *   orçamento mais recente entre as oferecidas; ignorada ao editar.
 * @param numeroSugerido - O número já calculado para um registro novo, por
 *   quem monta o componente; ignorado ao editar.
 * @param onSalvo - Chamado depois de gravar, para voltar à lista.
 * @param onCancelar - Chamado ao desistir, sem gravar nada.
 * @param ref - Recebe `AlcaDoFormularioOrcamento`, com `obterValores` — a
 *   leitura dos campos por fora do envio, que "Duplicar" usa.
 * @returns O cabeçalho em grade, a lista de itens e a barra de total.
 */
export const FormularioOrcamento = forwardRef<
  AlcaDoFormularioOrcamento,
  FormularioOrcamentoProps
>(function FormularioOrcamento(
  { orcamento, casas, casaSugerida, numeroSugerido, onSalvo, onCancelar },
  ref,
) {
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const salvar = useSalvarOrcamento();
  const consultaOrcamentos = useOrcamentos();

  const fornecedores = [
    ...new Set((consultaOrcamentos.data ?? []).map((item) => item.fornecedor)),
  ].sort((um, outro) => um.localeCompare(outro, 'pt-BR'));

  const form = useForm<OrcamentoNoFormulario>({
    resolver: zodResolver(novoOrcamentoSchema),
    defaultValues:
      orcamento === null
        ? {
            ...ORCAMENTO_EM_BRANCO,
            residenciaId: casaSugerida,
            numero: numeroSugerido,
          }
        : {
            residenciaId: orcamento.residencia.id,
            numero: orcamento.numero,
            data: orcamento.data,
            status: orcamento.status,
            categoria: orcamento.categoria,
            fornecedor: orcamento.fornecedor,
            responsavel: orcamento.responsavel ?? '',
            solicitante: orcamento.solicitante ?? '',
            pagamento: orcamento.pagamento,
            desconto: String(orcamento.desconto),
            observacao: orcamento.observacao ?? '',
            // A imagem fica de fora de propósito: ausente preserva a imagem
            // gravada, e é o `id` da linha que diz de qual item ela é.
            itens: orcamento.itens.map((item) => ({
              id: item.id,
              descricao: item.descricao,
              referencia: item.referencia ?? '',
              valor: String(item.valor),
            })),
          },
  });

  useImperativeHandle(ref, () => ({ obterValores: () => form.getValues() }), [form]);

  const linhas = useFieldArray({ control: form.control, name: 'itens' });
  const itensAoVivo = useWatch({ control: form.control, name: 'itens' }) ?? [];
  const descontoAoVivo = useWatch({ control: form.control, name: 'desconto' });
  const enviando = salvar.isPending;

  const soma = itensAoVivo.map((item) => ({ valor: dinheiroDoCampo(item?.valor) }));
  const bruto = soma.reduce((total, item) => total + item.valor, 0);
  const total = totalDoOrcamento(soma, dinheiroDoCampo(descontoAoVivo));

  const handleSalvar = form.handleSubmit(async (dados) => {
    setErroGeral(null);
    try {
      await salvar.mutateAsync({
        id: orcamento?.id ?? null,
        dados: novoOrcamentoSchema.parse(dados),
      });
      onSalvo();
    } catch (erro) {
      setErroGeral(aplicarErroDaApi(erro, form, tradutorPorMapa(CAMPOS)));
    }
  });

  return (
    <form onSubmit={(evento) => void handleSalvar(evento)}>
      <div className="mb-7 grid grid-cols-2 gap-4">
        <CampoDoCabecalho
          rotulo="Casa"
          htmlFor="orcamento-casa"
          erro={form.formState.errors.residenciaId?.message}
        >
          <Controller
            control={form.control}
            name="residenciaId"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange} disabled={enviando}>
                <SelectTrigger id="orcamento-casa" className="w-full">
                  <SelectValue placeholder="Para qual casa" />
                </SelectTrigger>
                <SelectContent>
                  {casas.map((casa) => (
                    <SelectItem key={casa.id} value={casa.id}>
                      {casa.apelido}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </CampoDoCabecalho>

        <CampoDoCabecalho rotulo="Categoria" htmlFor="orcamento-categoria">
          <Controller
            control={form.control}
            name="categoria"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange} disabled={enviando}>
                <SelectTrigger id="orcamento-categoria" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIA_ORCAMENTO.map((valor) => (
                    <SelectItem key={valor} value={valor}>
                      {ROTULOS_CATEGORIA[valor]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </CampoDoCabecalho>

        <CampoDoCabecalho
          rotulo="Número"
          htmlFor="orcamento-numero"
          erro={form.formState.errors.numero?.message}
        >
          <Controller
            control={form.control}
            name="numero"
            render={({ field }) => (
              <Input
                id="orcamento-numero"
                {...field}
                value={field.value ?? ''}
                disabled={enviando}
              />
            )}
          />
        </CampoDoCabecalho>

        <CampoDoCabecalho
          rotulo="Data"
          htmlFor="orcamento-data"
          erro={form.formState.errors.data?.message}
        >
          <Controller
            control={form.control}
            name="data"
            render={({ field }) => (
              <CampoMascarado
                id="orcamento-data"
                formato="data"
                value={field.value}
                onChange={field.onChange}
                disabled={enviando}
              />
            )}
          />
        </CampoDoCabecalho>

        <CampoDoCabecalho rotulo="Status" htmlFor="orcamento-status">
          <Controller
            control={form.control}
            name="status"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange} disabled={enviando}>
                <SelectTrigger id="orcamento-status" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_ORCAMENTO.map((valor) => (
                    <SelectItem key={valor} value={valor}>
                      {ROTULOS_STATUS[valor]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </CampoDoCabecalho>

        <CampoDoCabecalho rotulo="Por (responsáveis)" htmlFor="orcamento-responsavel">
          <Controller
            control={form.control}
            name="responsavel"
            render={({ field }) => (
              <Input
                id="orcamento-responsavel"
                placeholder="Ex: Fulana Souza e Beltrano Lima"
                {...field}
                value={field.value ?? ''}
                disabled={enviando}
              />
            )}
          />
        </CampoDoCabecalho>

        <CampoDoCabecalho
          rotulo="Fornecedor"
          htmlFor="orcamento-fornecedor"
          erro={form.formState.errors.fornecedor?.message}
        >
          <Controller
            control={form.control}
            name="fornecedor"
            render={({ field }) => (
              <Input
                id="orcamento-fornecedor"
                list="fornecedores-de-orcamento"
                placeholder="Ex: Nome do fornecedor"
                {...field}
                value={field.value ?? ''}
                disabled={enviando}
              />
            )}
          />
          <datalist id="fornecedores-de-orcamento">
            {fornecedores.map((nome) => (
              <option key={nome} value={nome} />
            ))}
          </datalist>
        </CampoDoCabecalho>

        <CampoDoCabecalho rotulo="Solicitante" htmlFor="orcamento-solicitante">
          <Controller
            control={form.control}
            name="solicitante"
            render={({ field }) => (
              <Input
                id="orcamento-solicitante"
                placeholder="Ex: Quem solicitou o orçamento"
                {...field}
                value={field.value ?? ''}
                disabled={enviando}
              />
            )}
          />
        </CampoDoCabecalho>

        <CampoDoCabecalho rotulo="Modo de pagamento" htmlFor="orcamento-pagamento">
          <Controller
            control={form.control}
            name="pagamento"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange} disabled={enviando}>
                <SelectTrigger id="orcamento-pagamento" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODO_PAGAMENTO_ORCAMENTO.map((valor) => (
                    <SelectItem key={valor} value={valor}>
                      {ROTULOS_PAGAMENTO[valor]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </CampoDoCabecalho>

        <CampoDoCabecalho rotulo="Valor de desconto (R$)" htmlFor="orcamento-desconto">
          <Controller
            control={form.control}
            name="desconto"
            render={({ field }) => (
              <CampoMascarado
                id="orcamento-desconto"
                formato="dinheiro"
                value={field.value ?? ''}
                onChange={field.onChange}
                disabled={enviando}
              />
            )}
          />
        </CampoDoCabecalho>

        <CampoDoCabecalho
          rotulo="Observação (opcional)"
          htmlFor="orcamento-observacao"
          className="col-span-2"
        >
          <Controller
            control={form.control}
            name="observacao"
            render={({ field }) => (
              <Textarea
                id="orcamento-observacao"
                rows={2}
                placeholder="Observações internas ou para o fornecedor"
                {...field}
                value={field.value ?? ''}
                disabled={enviando}
              />
            )}
          />
        </CampoDoCabecalho>
      </div>

      <CabecalhoDeSecao
        titulo="Itens do orçamento"
        acoes={
          <Button
            type="button"
            className="px-4 py-2 text-[13px]"
            onClick={() => linhas.append(ITEM_EM_BRANCO)}
            disabled={enviando}
          >
            + Adicionar item
          </Button>
        }
      />

      {linhas.fields.length === 0 ? (
        <Vazio mensagem="Nenhum item adicionado ainda." className="mb-6 p-[30px]" />
      ) : (
        <ul className="mb-6 list-none space-y-3.5 p-0">
          {linhas.fields.map((linha, indice) => {
            const idDoItem = form.getValues(caminhoDoItem(indice, 'id'));
            const gravado =
              typeof idDoItem === 'string'
                ? orcamento?.itens.find((item) => item.id === idDoItem)
                : undefined;

            return (
              <li
                key={linha.id}
                className="flex flex-col gap-4 rounded-md border border-linha-forte bg-superficie p-4 sm:flex-row sm:items-start"
              >
                <Controller
                  control={form.control}
                  name={caminhoDoItem(indice, 'foto')}
                  render={({ field, fieldState }) => (
                    <div className="shrink-0">
                      <label className="flex h-[88px] w-[88px] cursor-pointer flex-col items-center justify-center overflow-hidden rounded-md border border-dashed border-linha-forte p-1 text-center font-mono text-[10.5px] leading-snug text-texto-fraco transition-[border-color,color] duration-150 ease-in-out hover:border-brass hover:text-brass-light">
                        <input
                          ref={field.ref}
                          type="file"
                          accept="image/*"
                          aria-label={`Foto do item ${String(indice + 1)}`}
                          className="hidden"
                          disabled={enviando}
                          onChange={(evento) =>
                            handleArquivoDoItem(
                              evento,
                              (imagem) => {
                                form.clearErrors(caminhoDoItem(indice, 'foto'));
                                field.onChange(imagem);
                              },
                              (mensagem) =>
                                form.setError(caminhoDoItem(indice, 'foto'), {
                                  message: mensagem,
                                }),
                            )
                          }
                        />
                        {typeof field.value === 'string' && field.value !== '' ? (
                          <img
                            src={field.value}
                            alt="Foto do item"
                            className="h-full w-full object-cover"
                          />
                        ) : gravado?.temFoto === true ? (
                          <span>Foto salva. Clique para trocar.</span>
                        ) : (
                          <span>📷 Clique para adicionar foto</span>
                        )}
                      </label>
                      {fieldState.error !== undefined && (
                        <p className="mt-1 w-[88px] text-xs text-perigo">
                          {fieldState.error.message}
                        </p>
                      )}
                    </div>
                  )}
                />

                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <Label>Descrição</Label>
                  <Controller
                    control={form.control}
                    name={caminhoDoItem(indice, 'descricao')}
                    render={({ field, fieldState }) => (
                      <div>
                        <Textarea
                          aria-label={`Descrição do item ${String(indice + 1)}`}
                          placeholder="Marca, dimensões, características..."
                          rows={2}
                          {...field}
                          value={typeof field.value === 'string' ? field.value : ''}
                          disabled={enviando}
                        />
                        {fieldState.error !== undefined && (
                          <p className="mt-1 text-xs text-perigo">
                            {fieldState.error.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                  <Controller
                    control={form.control}
                    name={caminhoDoItem(indice, 'referencia')}
                    render={({ field, fieldState }) => (
                      <div>
                        <Input
                          aria-label={`Referência do item ${String(indice + 1)}`}
                          placeholder="Nº (ex: RDF08869)"
                          {...field}
                          value={typeof field.value === 'string' ? field.value : ''}
                          disabled={enviando}
                        />
                        {fieldState.error !== undefined && (
                          <p className="mt-1 text-xs text-perigo">
                            {fieldState.error.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                  {gravado?.temFoto === true && <ImagemDoItem itemId={gravado.id} />}
                </div>

                <div className="flex w-full flex-col gap-2 sm:w-[140px] sm:shrink-0">
                  <Label>Valor (R$)</Label>
                  <Controller
                    control={form.control}
                    name={caminhoDoItem(indice, 'valor')}
                    render={({ field, fieldState }) => (
                      <div>
                        <CampoMascarado
                          aria-label={`Valor do item ${String(indice + 1)}`}
                          formato="dinheiro"
                          value={
                            typeof field.value === 'string' || typeof field.value === 'number'
                              ? field.value
                              : ''
                          }
                          onChange={field.onChange}
                          disabled={enviando}
                        />
                        {fieldState.error !== undefined && (
                          <p className="mt-1 text-xs text-perigo">
                            {fieldState.error.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <Button
                  type="button"
                  size="icon-xs"
                  variant="destructive"
                  title="Remover item"
                  aria-label="Remover item"
                  onClick={() => linhas.remove(indice)}
                  disabled={enviando}
                >
                  🗑
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      {bruto !== total && (
        <p className="mb-2 text-right text-xs text-texto-fraco">
          soma dos itens {formatarDinheiro(bruto)}
        </p>
      )}
      <div className="flex items-center justify-between rounded-md border border-linha-forte bg-brass/8 px-[22px] py-4 font-mono text-[13px] tracking-[0.05em] text-texto-medio">
        <span>TOTAL</span>
        <span className="font-serif text-[22px] text-brass-light">
          {formatarDinheiro(total)}
        </span>
      </div>

      {erroGeral !== null && <p className="mt-4 text-sm text-perigo">{erroGeral}</p>}

      <div className="mt-6 flex flex-wrap justify-start gap-3">
        <Button type="submit" disabled={enviando}>
          {enviando ? 'Salvando…' : 'Salvar orçamento'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancelar} disabled={enviando}>
          Cancelar
        </Button>
      </div>
    </form>
  );
});
